'use client'

import { useTranslations } from 'next-intl'
import { Fragment } from 'react'
import { Button } from '@/components/button'
import { LoadingSpinner } from '@/components/loading-spinner'
import { RestaurantCard } from '@/features/restaurants/restaurant-card/restaurant-card'
import { RestaurantCardSkeleton } from '@/features/restaurants/restaurant-card/restaurant-card-skeleton'
import { RestaurantCardsProvider } from '@/features/restaurants/restaurant-card/restaurant-cards-context'
import { RestaurantCitySearchEmptyState } from '@/features/restaurants/restaurants-city-search/restaurant-city-search-empty-state'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { MobileBackButtonType, useMobileBackButtonInit } from '@/setup/back-button-init'
import { haversineDistanceKm } from '@/utils/functions/distance'
import { usePreloadImage } from '@/utils/image/use-preload-image'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

export const SEARCH_RESULTS_CLASS_NAME =
    'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pb-14'

export const RestaurantCitySearchResults = () => {
    const {
        restaurants,
        currency,
        coordinates,
        query: { citySlug },
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useRestaurantCitySearchContext()

    const t = useTranslations()

    // Initialize the mobile back button on the restaurant detail page on mobile, so that it will use router.back() instead of the href
    // Reason: When users come directly to the restaurant detail page, we want to make sure that the back button takes them back to the city page and not the previous page (which could be a different website like google)
    // To achieve that, we use sessionStorage to store a flag that tells the back button to use router.back() instead of the href
    useMobileBackButtonInit(MobileBackButtonType.RESTAURANT_DETAIL)

    // Preload gradient for restaurant detail page
    usePreloadImage('/_images/meta/fade.svg')

    return (
        <div className="w-full flex flex-col">
            <div className={cn(SEARCH_RESULTS_CLASS_NAME, 'pb-0')}>
                <RestaurantCardsProvider styleType="default">
                    {run(() => {
                        if (!restaurants) {
                            return (
                                <Fragment>
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                </Fragment>
                            )
                        }

                        if (!restaurants.length) {
                            return <RestaurantCitySearchEmptyState />
                        }

                        return restaurants?.map((restaurant, index) => {
                            const distanceKm = coordinates
                                ? haversineDistanceKm(
                                      {
                                          lat: Number(coordinates.lat),
                                          lng: Number(coordinates.lng),
                                      },
                                      { lat: restaurant.latitude, lng: restaurant.longitude }
                                  )
                                : undefined

                            return (
                                <RestaurantCard
                                    index={index}
                                    loadWithPriority
                                    citySlug={citySlug}
                                    key={'restaurant-city-search-results' + restaurant.uuid}
                                    restaurant={restaurant}
                                    currency={currency}
                                    distanceKm={distanceKm}
                                />
                            )
                        })
                    })}
                </RestaurantCardsProvider>
            </div>

            {/* Paged via button instead of infinite scroll, so the links and
                footer below the grid stay reachable */}
            {(hasNextPage || isFetchingNextPage) && (
                <div className="mt-6 flex min-h-[52px] w-full items-center justify-center">
                    {isFetchingNextPage ? (
                        <LoadingSpinner color={COLORS.black} size={32} />
                    ) : (
                        <div className="w-full md:w-[400px]">
                            <Button
                                styleType="secondary"
                                width="100%"
                                onClick={() => fetchNextPage()}
                            >
                                {t('restaurants.city.loadMore')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
